// Typed API client for the Worker API. All AI calls go through the Worker.

import type {
  ApiError,
  AssetRef,
  Chart,
  CreateProjectRequest,
  DocumentType,
  ExportResponse,
  OutputLanguage,
  Page,
  Project,
  ProjectDetail,
  ProjectSummary,
  Roadmap,
} from "@asmg/shared";

const BASE = import.meta.env.VITE_API_BASE_URL ?? "";

/** Error thrown by the client carrying the parsed API error envelope. */
export class ApiClientError extends Error {
  constructor(public status: number, public payload: ApiError | undefined, message: string) {
    super(message);
    this.name = "ApiClientError";
  }
  get reason() {
    return this.payload?.error.reason;
  }
  get retryable() {
    return this.payload?.error.retryable ?? true;
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const payload = (data as ApiError | undefined)?.error ? (data as ApiError) : undefined;
    throw new ApiClientError(res.status, payload, payload?.error.message ?? `HTTP ${res.status}`);
  }
  return data as T;
}

export const api = {
  // Projects
  listProjects: () => req<ProjectSummary[]>("/api/projects"),
  getProject: (id: string) => req<ProjectDetail>(`/api/projects/${id}`),
  createProject: (body: CreateProjectRequest) =>
    req<Project>("/api/projects", { method: "POST", body: JSON.stringify(body) }),
  deleteProject: (id: string) => req<void>(`/api/projects/${id}`, { method: "DELETE" }),
  updateSettings: (id: string, body: { documentType?: DocumentType; language?: OutputLanguage }) =>
    req<Project>(`/api/projects/${id}/settings`, { method: "PATCH", body: JSON.stringify(body) }),

  // Roadmap
  generateRoadmap: (id: string) => req<Roadmap>(`/api/projects/${id}/roadmap`, { method: "POST" }),
  refineRoadmap: (id: string, body: { suggestion?: string; roadmap?: Roadmap }) =>
    req<Roadmap>(`/api/projects/${id}/roadmap`, { method: "PATCH", body: JSON.stringify(body) }),
  confirmRoadmap: (id: string) =>
    req<Project>(`/api/projects/${id}/roadmap/confirm`, { method: "POST" }),

  // Document
  generateDocument: (id: string) =>
    req<ProjectDetail>(`/api/projects/${id}/document`, { method: "POST" }),

  // Charts
  regenerateChart: (id: string, chartId: string, instruction?: string) =>
    req<Chart>(`/api/projects/${id}/charts/${chartId}/regenerate`, {
      method: "POST",
      body: JSON.stringify({ instruction }),
    }),

  // Images
  generatePageImage: (id: string, pageId: string, description: string) =>
    req<AssetRef>(`/api/projects/${id}/pages/${pageId}/image`, {
      method: "POST",
      body: JSON.stringify({ description }),
    }),
  generateAssetImage: (id: string, assetId: string, body: { description?: string; instruction?: string }) =>
    req<AssetRef>(`/api/projects/${id}/assets/${assetId}/image`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  assetUrl: (assetId: string) => `${BASE}/api/assets/${assetId}`,

  // Pages
  aiEditPage: (id: string, pageId: string, instruction: string) =>
    req<Page>(`/api/projects/${id}/pages/${pageId}/ai-edit`, {
      method: "POST",
      body: JSON.stringify({ instruction }),
    }),
  inlineEditPage: (id: string, pageId: string, contentHtml: string) =>
    req<Page>(`/api/projects/${id}/pages/${pageId}`, {
      method: "PUT",
      body: JSON.stringify({ contentHtml }),
    }),

  // Export
  exportPdf: (id: string) => req<ExportResponse>(`/api/projects/${id}/export`, { method: "POST" }),
  exportUrl: (id: string, exportId: string) => `${BASE}/api/projects/${id}/exports/${exportId}`,

  // Models
  listModels: () => req<{ models: string[] }>("/api/models"),
};

export interface StreamHandlers {
  onStatus?: (msg: string) => void;
  onDelta?: (text: string) => void;
  onUnit?: (info: { index: number; total: number; title: string }) => void;
  onDone?: (data: unknown) => void;
  onError?: (message: string) => void;
}

/** Consume a Server-Sent-Events generation stream from the Worker. */
export async function streamGenerate(path: string, handlers: StreamHandlers): Promise<void> {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
  });
  if (!res.ok || !res.body) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = (await res.json()) as ApiError;
      msg = j.error?.message ?? j.error?.reason ?? msg;
    } catch {
      /* ignore */
    }
    handlers.onError?.(msg);
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split("\n\n");
    buffer = chunks.pop() ?? "";
    for (const chunk of chunks) {
      const evLine = chunk.split("\n").find((l) => l.startsWith("event:"));
      const dataLine = chunk.split("\n").find((l) => l.startsWith("data:"));
      if (!evLine || !dataLine) continue;
      const event = evLine.slice(6).trim();
      let data: unknown;
      try {
        data = JSON.parse(dataLine.slice(5).trim());
      } catch {
        /* ignore */
      }
      if (event === "status") handlers.onStatus?.((data as { message: string }).message);
      else if (event === "delta") handlers.onDelta?.((data as { text: string }).text);
      else if (event === "unit") handlers.onUnit?.(data as { index: number; total: number; title: string });
      else if (event === "done") handlers.onDone?.(data);
      else if (event === "error") handlers.onError?.((data as { message: string }).message);
    }
  }
}
